/**
 * ProjectsTab
 * Projects listing with search, filters, pagination, column visibility, and view modes
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjects } from '../../../context/ProjectContext';
import { useAuth } from '../../../context/AuthContext';
import { WORKFLOW_STATUS, WORKFLOW_STATUS_LABELS, PROJECT_PHASE, PHASE_LABELS } from '../../../utils/constants';
import {
  formatAgentNames,
  formatLeaderNames,
  formatManagerNames,
  formatSidAssignment,
  formatProjectLocation
} from '../../../utils/projectDataUtils';
import { useDebounce } from '../../../hooks/useDebounce';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import ProjectsTable from './shared/ProjectsTable';
import ProjectSearchBar from './shared/ProjectSearchBar';
import ColumnVisibilityToggle from './shared/ColumnVisibilityToggle';
import ViewModeToggle from './shared/ViewModeToggle';
import ProjectCardList from './shared/ProjectCardList';
import Pagination from '../../common/Pagination';
import PendingSalesFormsWidget from '../widgets/PendingSalesFormsWidget';
import { hasDirectorAccess } from '../../../utils/rolePermissions';

// Workflow order for phases (for sorting)
const PHASE_WORKFLOW_ORDER = [
  PROJECT_PHASE.SALES,
  PROJECT_PHASE.MEASUREMENT,
  PROJECT_PHASE.TWO_D,
  PROJECT_PHASE.THREE_D,
  PROJECT_PHASE.DD,
  PROJECT_PHASE.QUOTATION,
  PROJECT_PHASE.CONTRACT,
  PROJECT_PHASE.CLOSED
];

// Workflow order for all statuses (for sorting)
const STATUS_WORKFLOW_ORDER = [
  // Sales
  WORKFLOW_STATUS.PENDING_PROJECT_NUMBER,
  WORKFLOW_STATUS.ACTIVE_PROJECT,
  // Measurement
  WORKFLOW_STATUS.MEASUREMENT_PENDING,
  WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS,
  WORKFLOW_STATUS.MEASUREMENT_DONE,
  // 2D
  WORKFLOW_STATUS.TWO_D_PENDING,
  WORKFLOW_STATUS.TWO_D_IN_PROGRESS,
  WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW,
  WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED,
  WORKFLOW_STATUS.TWO_D_APPROVED,
  // 3D
  WORKFLOW_STATUS.THREE_D_PENDING,
  WORKFLOW_STATUS.THREE_D_IN_PROGRESS,
  WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW,
  WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED,
  WORKFLOW_STATUS.THREE_D_APPROVED,
  // DD
  WORKFLOW_STATUS.DD_IN_PROGRESS,
  WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION,
  // Quotation
  WORKFLOW_STATUS.QUOTATION_IN_PROGRESS,
  // Contract
  WORKFLOW_STATUS.READY_FOR_SIGNING,
  WORKFLOW_STATUS.SIGNING_SCHEDULED,
  WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED,
  // Closed
  WORKFLOW_STATUS.PROJECT_CLOSED_WON,
  WORKFLOW_STATUS.PROJECT_CLOSED_LOST,
  WORKFLOW_STATUS.PROJECT_CANCELLED
];

// Map phases to their corresponding statuses
const PHASE_STATUS_MAP = {
  [PROJECT_PHASE.SALES]: [
    WORKFLOW_STATUS.PENDING_PROJECT_NUMBER,
    WORKFLOW_STATUS.ACTIVE_PROJECT
  ],
  [PROJECT_PHASE.MEASUREMENT]: [
    WORKFLOW_STATUS.MEASUREMENT_PENDING,
    WORKFLOW_STATUS.MEASUREMENT_IN_PROGRESS,
    WORKFLOW_STATUS.MEASUREMENT_DONE
  ],
  [PROJECT_PHASE.TWO_D]: [
    WORKFLOW_STATUS.TWO_D_PENDING,
    WORKFLOW_STATUS.TWO_D_IN_PROGRESS,
    WORKFLOW_STATUS.TWO_D_PENDING_CLIENT_REVIEW,
    WORKFLOW_STATUS.TWO_D_AMENDMENT_REQUIRED,
    WORKFLOW_STATUS.TWO_D_APPROVED
  ],
  [PROJECT_PHASE.THREE_D]: [
    WORKFLOW_STATUS.THREE_D_PENDING,
    WORKFLOW_STATUS.THREE_D_IN_PROGRESS,
    WORKFLOW_STATUS.THREE_D_PENDING_CLIENT_REVIEW,
    WORKFLOW_STATUS.THREE_D_AMENDMENT_REQUIRED,
    WORKFLOW_STATUS.THREE_D_APPROVED
  ],
  [PROJECT_PHASE.DD]: [
    WORKFLOW_STATUS.DD_IN_PROGRESS,
    WORKFLOW_STATUS.DD_SUBMITTED_TO_QUOTATION
  ],
  [PROJECT_PHASE.QUOTATION]: [
    WORKFLOW_STATUS.QUOTATION_IN_PROGRESS
  ],
  [PROJECT_PHASE.CONTRACT]: [
    WORKFLOW_STATUS.READY_FOR_SIGNING,
    WORKFLOW_STATUS.SIGNING_SCHEDULED,
    WORKFLOW_STATUS.SIGNING_AMENDMENT_REQUIRED
  ],
  [PROJECT_PHASE.CLOSED]: [
    WORKFLOW_STATUS.PROJECT_CLOSED_WON,
    WORKFLOW_STATUS.PROJECT_CLOSED_LOST,
    WORKFLOW_STATUS.PROJECT_CANCELLED
  ]
};

// Default column visibility
const DEFAULT_COLUMN_VISIBILITY = {
  projectNumber: true,
  status: true,
  packagePrice: true,
  location: true,
  propertyType: true,
  salesTeam: true,
  sid: true,
  createdAt: true
};

export default function ProjectsTab() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const {
    projects,
    loading,
    error,
    fetchMyProjects
  } = useProjects();

  // Check if user is director, manager or leader for showing pending sales forms
  const showPendingSalesForms = hasDirectorAccess(user?.role) || user?.role === 'manager' || user?.role === 'leader';

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 300);

  // Filter state
  const [filters, setFilters] = useState({
    phase: '',
    status: '',
    agent: '',
    leader: '',
    manager: '',
    packagePriceMin: '',
    packagePriceMax: '',
    sid: ''
  });

  // Pagination state
  const [pageSize, setPageSize] = useLocalStorage('projectsPageSize', 25);
  const [currentPage, setCurrentPage] = useState(1);

  // Column visibility state
  const [columnVisibility, setColumnVisibility] = useLocalStorage(
    'projectsColumnVisibility',
    DEFAULT_COLUMN_VISIBILITY
  );

  // View mode state (table vs card)
  const [viewMode, setViewMode] = useLocalStorage('projectsViewMode', 'table');

  // Mobile detection - auto-switch to cards on small screens
  const isMobile = useMediaQuery('(max-width: 767px)');
  const effectiveViewMode = isMobile ? 'card' : viewMode;

  // Mobile filters collapse state
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Mobile sort state
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showSortMenu, setShowSortMenu] = useState(false);

  // Sort options for mobile card view
  const SORT_OPTIONS = [
    { field: 'createdAt', label: 'Date Created' },
    { field: 'projectNumber', label: 'Project Number' },
    { field: 'currentPackagePrice', label: 'Package Price' },
    { field: 'workflowStatus', label: 'Status' }
  ];

  useEffect(() => {
    fetchMyProjects();
  }, [fetchMyProjects]);

  // Auto-expand filters on desktop, collapse on mobile
  useEffect(() => {
    setFiltersExpanded(!isMobile);
  }, [isMobile]);

  // Reset to page 1 when filters, search, or sort change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, debouncedSearch, sortField, sortDirection]);

  // Get available statuses based on selected phase
  const availableStatuses = useMemo(() => {
    if (!filters.phase) {
      // No phase selected - show all statuses that exist in data, sorted by workflow order
      if (!projects || projects.length === 0) return [];
      const statusesSet = new Set();
      projects.forEach(project => {
        if (project.workflowStatus) {
          statusesSet.add(project.workflowStatus);
        }
      });
      // Sort by workflow order
      return Array.from(statusesSet).sort((a, b) => {
        const indexA = STATUS_WORKFLOW_ORDER.indexOf(a);
        const indexB = STATUS_WORKFLOW_ORDER.indexOf(b);
        // If status not in order array, put it at the end
        return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
      });
    }

    // Phase selected - show only statuses for that phase that exist in data
    // PHASE_STATUS_MAP already has statuses in workflow order
    const phaseStatuses = PHASE_STATUS_MAP[filters.phase] || [];
    if (!projects || projects.length === 0) return phaseStatuses;

    // Filter to only show statuses that exist in the data (order preserved from PHASE_STATUS_MAP)
    const existingStatuses = new Set(projects.map(p => p.workflowStatus));
    return phaseStatuses.filter(status => existingStatuses.has(status));
  }, [filters.phase, projects]);

  // Extract unique values for filter dropdowns with hierarchy relationships
  const filterOptions = useMemo(() => {
    if (!projects || projects.length === 0) return { agents: [], leaders: [], managers: [], sids: [], phases: [], managerLeaderMap: {}, leaderAgentMap: {} };

    const managersSet = new Set();
    const sidsSet = new Set();
    const phasesSet = new Set();

    // Maps to track hierarchy: manager -> leaders, leader -> agents
    const managerLeaderMap = {}; // { managerName: Set of leaderNames }
    const leaderAgentMap = {}; // { leaderName: Set of agentNames }

    projects.forEach(project => {
      // Process salesAgents to build hierarchy
      if (project.salesAgents && project.salesAgents.length > 0) {
        project.salesAgents
          .filter(sa => sa.isActive !== false)
          .forEach(sa => {
            const managerName = sa.manager ? (sa.manager.nickname || sa.manager.fullName) : null;
            const leaderName = sa.salesLeader ? (sa.salesLeader.nickname || sa.salesLeader.fullName) : null;
            const agentName = sa.agent ? (sa.agent.nickname || sa.agent.fullName) : null;

            // Add manager
            if (managerName) {
              managersSet.add(managerName);
              if (!managerLeaderMap[managerName]) {
                managerLeaderMap[managerName] = new Set();
              }
              if (leaderName) {
                managerLeaderMap[managerName].add(leaderName);
              }
            }

            // Add leader -> agent mapping
            if (leaderName) {
              if (!leaderAgentMap[leaderName]) {
                leaderAgentMap[leaderName] = new Set();
              }
              if (agentName) {
                leaderAgentMap[leaderName].add(agentName);
              }
            }
          });
      }

      // Fallback to salesForm for agent/leader if no salesAgents
      if ((!project.salesAgents || project.salesAgents.length === 0) && project.salesForm?.agent) {
        const agentName = project.salesForm.agent.nickname || project.salesForm.agent.fullName;
        const leaderName = project.salesForm.agent.leader
          ? (project.salesForm.agent.leader.nickname || project.salesForm.agent.leader.fullName)
          : null;

        if (leaderName && agentName) {
          if (!leaderAgentMap[leaderName]) {
            leaderAgentMap[leaderName] = new Set();
          }
          leaderAgentMap[leaderName].add(agentName);
        }
      }

      // SIDs
      const sidName = formatSidAssignment(project.teamMembers);
      if (sidName && sidName !== 'Unassigned') {
        const mainName = sidName.split(' (')[0];
        sidsSet.add(mainName);
      }

      // Phases - determine from workflow status
      if (project.workflowStatus) {
        for (const [phase, statuses] of Object.entries(PHASE_STATUS_MAP)) {
          if (statuses.includes(project.workflowStatus)) {
            phasesSet.add(phase);
            break;
          }
        }
      }
    });

    // Sort phases by workflow order
    const sortedPhases = Array.from(phasesSet).sort((a, b) => {
      const indexA = PHASE_WORKFLOW_ORDER.indexOf(a);
      const indexB = PHASE_WORKFLOW_ORDER.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

    // Convert Sets to arrays in maps
    const managerLeaderMapArrays = {};
    Object.keys(managerLeaderMap).forEach(manager => {
      managerLeaderMapArrays[manager] = Array.from(managerLeaderMap[manager]).sort();
    });

    const leaderAgentMapArrays = {};
    Object.keys(leaderAgentMap).forEach(leader => {
      leaderAgentMapArrays[leader] = Array.from(leaderAgentMap[leader]).sort();
    });

    // Get all unique leaders and agents for when no filter is applied
    const allLeaders = new Set();
    const allAgents = new Set();
    Object.values(managerLeaderMap).forEach(leaders => {
      leaders.forEach(l => allLeaders.add(l));
    });
    Object.values(leaderAgentMap).forEach(agents => {
      agents.forEach(a => allAgents.add(a));
    });

    return {
      managers: Array.from(managersSet).sort(),
      leaders: Array.from(allLeaders).sort(),
      agents: Array.from(allAgents).sort(),
      sids: Array.from(sidsSet).sort(),
      phases: sortedPhases,
      managerLeaderMap: managerLeaderMapArrays,
      leaderAgentMap: leaderAgentMapArrays
    };
  }, [projects]);

  // Get available leaders based on selected manager (cascading filter)
  const availableLeaders = useMemo(() => {
    if (!filters.manager) {
      return filterOptions.leaders;
    }
    return filterOptions.managerLeaderMap[filters.manager] || [];
  }, [filters.manager, filterOptions.leaders, filterOptions.managerLeaderMap]);

  // Get available agents based on selected leader (cascading filter)
  const availableAgents = useMemo(() => {
    if (!filters.leader) {
      // If manager is selected but not leader, show agents under all leaders of that manager
      if (filters.manager) {
        const leadersUnderManager = filterOptions.managerLeaderMap[filters.manager] || [];
        const agentsSet = new Set();
        leadersUnderManager.forEach(leader => {
          const agents = filterOptions.leaderAgentMap[leader] || [];
          agents.forEach(a => agentsSet.add(a));
        });
        return Array.from(agentsSet).sort();
      }
      return filterOptions.agents;
    }
    return filterOptions.leaderAgentMap[filters.leader] || [];
  }, [filters.manager, filters.leader, filterOptions.agents, filterOptions.managerLeaderMap, filterOptions.leaderAgentMap]);

  // Filter and search projects
  const filteredProjects = useMemo(() => {
    if (!projects) return [];

    let result = [...projects];

    // Search filter
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();
      result = result.filter(p => {
        const projectNumber = (p.projectNumber || '').toLowerCase();
        const clientName = (p.salesForm?.clientName || '').toLowerCase();
        const address = formatProjectLocation(p.salesForm).toLowerCase();
        return projectNumber.includes(query) ||
               clientName.includes(query) ||
               address.includes(query);
      });
    }

    // Phase filter - filter by statuses in that phase
    if (filters.phase) {
      const phaseStatuses = PHASE_STATUS_MAP[filters.phase] || [];
      result = result.filter(p => phaseStatuses.includes(p.workflowStatus));
    }

    // Status filter
    if (filters.status) {
      result = result.filter(p => p.workflowStatus === filters.status);
    }

    if (filters.agent) {
      result = result.filter(p => {
        const agentName = formatAgentNames(p.salesAgents, p.salesForm);
        return agentName.toLowerCase().includes(filters.agent.toLowerCase());
      });
    }

    if (filters.leader) {
      result = result.filter(p => {
        const leaderName = formatLeaderNames(p.salesAgents, p.salesForm);
        return leaderName.toLowerCase().includes(filters.leader.toLowerCase());
      });
    }

    if (filters.manager) {
      result = result.filter(p => {
        const managerName = formatManagerNames(p.salesAgents);
        return managerName.toLowerCase().includes(filters.manager.toLowerCase());
      });
    }

    if (filters.packagePriceMin) {
      const min = parseFloat(filters.packagePriceMin);
      result = result.filter(p => (p.currentPackagePrice || 0) >= min);
    }

    if (filters.packagePriceMax) {
      const max = parseFloat(filters.packagePriceMax);
      result = result.filter(p => (p.currentPackagePrice || 0) <= max);
    }

    if (filters.sid) {
      result = result.filter(p => {
        const sidName = formatSidAssignment(p.teamMembers);
        return sidName.toLowerCase().includes(filters.sid.toLowerCase());
      });
    }

    return result;
  }, [projects, filters, debouncedSearch]);

  // Helper function to get sort value for a project
  const getSortValue = (project, field) => {
    switch (field) {
      case 'createdAt':
        return new Date(project.createdAt).getTime();
      case 'currentPackagePrice':
      case 'packagePrice':
        return project.currentPackagePrice || 0;
      case 'projectNumber':
        return project.projectNumber || '';
      case 'workflowStatus':
      case 'status':
        const idx = STATUS_WORKFLOW_ORDER.indexOf(project.workflowStatus);
        return idx === -1 ? 999 : idx;
      case 'location':
        return formatProjectLocation(project.salesForm);
      case 'propertyType':
        return project.salesForm?.propertyType || '';
      case 'salesTeam':
        return formatAgentNames(project.salesAgents, project.salesForm);
      case 'sid':
        return formatSidAssignment(project.teamMembers);
      default:
        return '';
    }
  };

  // Sort and paginate filtered projects (sorting applied for BOTH views before pagination)
  const paginatedProjects = useMemo(() => {
    let result = [...filteredProjects];

    // Apply sorting for both table and card views BEFORE pagination
    result.sort((a, b) => {
      const aVal = getSortValue(a, sortField);
      const bVal = getSortValue(b, sortField);

      let comparison;
      if (typeof aVal === 'number') {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    const startIndex = (currentPage - 1) * pageSize;
    return result.slice(startIndex, startIndex + pageSize);
  }, [filteredProjects, currentPage, pageSize, sortField, sortDirection]);

  // Handle filter change
  const handleFilterChange = (field, value) => {
    if (field === 'phase') {
      // When phase changes, clear the status filter
      setFilters(prev => ({ ...prev, phase: value, status: '' }));
    } else if (field === 'manager') {
      // When manager changes, clear leader and agent filters
      setFilters(prev => ({ ...prev, manager: value, leader: '', agent: '' }));
    } else if (field === 'leader') {
      // When leader changes, clear agent filter
      setFilters(prev => ({ ...prev, leader: value, agent: '' }));
    } else {
      setFilters(prev => ({ ...prev, [field]: value }));
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setFilters({
      phase: '',
      status: '',
      agent: '',
      leader: '',
      manager: '',
      packagePriceMin: '',
      packagePriceMax: '',
      sid: ''
    });
  };

  // Toggle column visibility
  const handleColumnToggle = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Handle page size change
  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  // Check if any filters are active
  const hasActiveFilters = Object.values(filters).some(v => v !== '') || debouncedSearch !== '';

  // Count active filters for mobile badge
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.phase) count++;
    if (filters.status) count++;
    if (filters.agent) count++;
    if (filters.leader) count++;
    if (filters.manager) count++;
    if (filters.packagePriceMin) count++;
    if (filters.packagePriceMax) count++;
    if (filters.sid) count++;
    return count;
  }, [filters]);

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  return (
    <div>
      {/* Direct Booking Button - For Directors, Managers, Leaders */}
      {showPendingSalesForms && (
        <div className="mb-4">
          <button
            onClick={() => navigate('/sales-forms/new')}
            className={`flex items-center justify-center gap-2 px-5 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98] ${
              isMobile ? 'w-full' : ''
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Direct Booking
          </button>
        </div>
      )}

      {/* Pending Sales Forms Widget - Manager/Leader only */}
      {showPendingSalesForms && <PendingSalesFormsWidget />}

      {/* Search and Controls Row */}
      <div className={isMobile ? "mb-4" : "card mb-4"}>
        <div className="flex items-center gap-2">
          {/* Search Bar - takes most space */}
          <div className="flex-1 min-w-0">
            <ProjectSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={isMobile ? "Search..." : "Search by project number, client name, or address..."}
              isMobile={isMobile}
            />
          </div>

          {/* Mobile: Sort + Filter icon buttons */}
          {isMobile && (
            <>
              {/* Sort Button - compact icon */}
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="relative flex items-center justify-center p-2.5 bg-white shadow-md border border-gray-100 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:shadow-sm min-w-[44px] min-h-[44px] touch-manipulation transition-all"
                aria-label="Sort options"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                {/* Direction indicator */}
                <span className="absolute -bottom-0.5 -right-0.5 text-[10px] font-bold text-blue-600">
                  {sortDirection === 'asc' ? '↑' : '↓'}
                </span>
              </button>

              {/* Filter Button - icon with badge */}
              <button
                onClick={() => setFiltersExpanded(!filtersExpanded)}
                className={`relative flex items-center justify-center p-2.5 bg-white shadow-md border border-gray-100 rounded-xl hover:bg-gray-50 active:bg-gray-100 active:shadow-sm min-w-[44px] min-h-[44px] touch-manipulation transition-all ${
                  filtersExpanded ? 'ring-2 ring-blue-500 ring-offset-1' : ''
                }`}
                aria-label="Toggle filters"
              >
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
                {/* Active filter count badge */}
                {activeFilterCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 bg-blue-500 text-white text-[10px] font-bold rounded-full">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </>
          )}

          {/* Desktop: View Controls */}
          {!isMobile && (
            <div className="flex items-center gap-2">
              <ColumnVisibilityToggle
                visibility={columnVisibility}
                onToggle={handleColumnToggle}
              />
              <ViewModeToggle
                mode={viewMode}
                onChange={setViewMode}
              />
            </div>
          )}
        </div>

        {/* Sort dropdown menu - positioned below toolbar */}
        {isMobile && showSortMenu && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-10"
              onClick={() => setShowSortMenu(false)}
            />

            {/* Menu */}
            <div className="absolute left-4 right-4 mt-2 bg-white rounded-xl shadow-lg border border-gray-200 z-20 py-1 max-h-[60vh] overflow-y-auto">
              {SORT_OPTIONS.map(option => (
                <button
                  key={option.field}
                  onClick={() => {
                    if (sortField === option.field) {
                      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField(option.field);
                      setSortDirection('desc');
                    }
                    setShowSortMenu(false);
                  }}
                  className={`w-full px-4 py-3.5 text-left text-sm flex items-center justify-between active:bg-gray-100 touch-manipulation ${
                    sortField === option.field ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                  }`}
                >
                  <span>{option.label}</span>
                  {sortField === option.field && (
                    <span className="text-blue-600 font-medium flex items-center gap-1">
                      {sortDirection === 'asc' ? '↑' : '↓'}
                      <span className="text-xs">{sortDirection === 'asc' ? 'Asc' : 'Desc'}</span>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Filters Card - Controlled by toolbar button on mobile */}
      <div
        className={`overflow-hidden transition-all duration-300 ease-in-out ${
          isMobile
            ? (filtersExpanded ? 'max-h-[600px] opacity-100 mb-4' : 'max-h-0 opacity-0 mb-0')
            : 'card mb-6'
        }`}
      >
        <div className={isMobile ? "bg-white rounded-xl shadow-md border border-gray-100 p-3" : ""}>
          {/* Desktop header */}
          <div className="hidden md:flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Filters</h3>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Mobile header with clear button */}
          {isMobile && hasActiveFilters && (
            <div className="flex items-center justify-between mb-3 pb-2 border-b border-gray-100">
              <span className="text-sm font-medium text-gray-700">Filters</span>
              <button
                onClick={clearFilters}
                className="text-sm text-blue-600 hover:text-blue-800 active:text-blue-900 touch-manipulation"
              >
                Clear all
              </button>
            </div>
          )}

          {/* Filter grid - responsive: 1 col on tiny, 2 cols on mobile, 4 on tablet, 9 on desktop */}
          <div className="grid grid-cols-1 xs:grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3 xs:gap-4">
          {/* Phase filter */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Phase</label>
            <select
              value={filters.phase}
              onChange={(e) => handleFilterChange('phase', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
            >
              <option value="">All Phases</option>
              {filterOptions.phases.map(phase => (
                <option key={phase} value={phase}>{PHASE_LABELS[phase] || phase}</option>
              ))}
            </select>
          </div>

          {/* Status filter - Cascades from Phase */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className={`w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation ${
                availableStatuses.length === 0 ? 'bg-gray-50 text-gray-400' : ''
              }`}
              disabled={availableStatuses.length === 0}
            >
              <option value="">All Statuses</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>
                  {WORKFLOW_STATUS_LABELS[status] || status}
                </option>
              ))}
            </select>
          </div>

          {/* Manager filter */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Manager</label>
            <select
              value={filters.manager}
              onChange={(e) => handleFilterChange('manager', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
            >
              <option value="">All Managers</option>
              {filterOptions.managers.map(manager => (
                <option key={manager} value={manager}>{manager}</option>
              ))}
            </select>
          </div>

          {/* Leader filter - Cascades from Manager */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Leader</label>
            <select
              value={filters.leader}
              onChange={(e) => handleFilterChange('leader', e.target.value)}
              className={`w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation ${
                availableLeaders.length === 0 ? 'bg-gray-50 text-gray-400' : ''
              }`}
              disabled={availableLeaders.length === 0}
            >
              <option value="">All Leaders</option>
              {availableLeaders.map(leader => (
                <option key={leader} value={leader}>{leader}</option>
              ))}
            </select>
          </div>

          {/* Agent filter - Cascades from Leader */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Agent</label>
            <select
              value={filters.agent}
              onChange={(e) => handleFilterChange('agent', e.target.value)}
              className={`w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation ${
                availableAgents.length === 0 ? 'bg-gray-50 text-gray-400' : ''
              }`}
              disabled={availableAgents.length === 0}
            >
              <option value="">All Agents</option>
              {availableAgents.map(agent => (
                <option key={agent} value={agent}>{agent}</option>
              ))}
            </select>
          </div>

          {/* Package Price Min */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Min Price</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="RM 0"
              value={filters.packagePriceMin}
              onChange={(e) => handleFilterChange('packagePriceMin', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
            />
          </div>

          {/* Package Price Max */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">Max Price</label>
            <input
              type="number"
              inputMode="numeric"
              placeholder="No limit"
              value={filters.packagePriceMax}
              onChange={(e) => handleFilterChange('packagePriceMax', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
            />
          </div>

          {/* SID filter */}
          <div>
            <label className="block text-[11px] xs:text-xs font-medium text-gray-600 mb-1">SID</label>
            <select
              value={filters.sid}
              onChange={(e) => handleFilterChange('sid', e.target.value)}
              className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 touch-manipulation"
            >
              <option value="">All SIDs</option>
              {filterOptions.sids.map(sid => (
                <option key={sid} value={sid}>{sid}</option>
              ))}
            </select>
          </div>
        </div>{/* End filter grid */}
        </div>{/* End mobile card wrapper */}
      </div>{/* End filters section */}

      {/* Projects Table/Card View */}
      {effectiveViewMode === 'table' ? (
        // Table view - keep card wrapper for desktop
        <div className="card">
          <ProjectsTable
            projects={paginatedProjects}
            loading={loading}
            visibleColumns={columnVisibility}
            emptyMessage={
              hasActiveFilters
                ? "No projects match the current filters"
                : "You don't have any assigned projects yet"
            }
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={(field) => {
              if (sortField === field) {
                setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
              } else {
                setSortField(field);
                setSortDirection('asc');
              }
            }}
          />
          {filteredProjects.length > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={filteredProjects.length}
              pageSize={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      ) : (
        // Card view - no wrapper, cards stand alone on mobile
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <ProjectCardList
              projects={paginatedProjects}
              emptyMessage={
                hasActiveFilters
                  ? "No projects match the current filters"
                  : "You don't have any assigned projects yet"
              }
            />
          )}
          {filteredProjects.length > 0 && (
            <div className="mt-4 bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <Pagination
                currentPage={currentPage}
                totalItems={filteredProjects.length}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
